import { Button, CircularProgress, TextField, Typography } from '@mui/material';
import * as Yup from 'yup';
import React, { useState } from 'react';
import { Else, If, Then } from 'react-if';
import { Link } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';

import TokenIcon from '../../../assets/icons/token-icon';
import { BackButton } from '../../../components/back-button';
import useTimer from '../../../hooks/useTimer';
import * as S from './styles';
import UserFirstLoginLayout from '../user-first-login/layout/user-first-login-layout';
import { AuthService } from '../../../services/auth.service';

export const ForgotPasswordScreen: React.FC = () => {
    const [verifyEmailPage, setVerifyEmailPage] = useState(false);

    const [loadingSendForgot, setLoadingSendForgot] = useState(false);
    const { timeLeft, restartTimer, counting } = useTimer(60);

    const loginSchema = Yup.object().shape({
        email: Yup
            .string()
            .email('Incorrect email')
            .required('Type your email'),
    });

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: loginSchema,
        onSubmit: async (values) => {
            setLoadingSendForgot(true);
            await AuthService.recoverPassword(values.email, (isValid, msg) => {
                setLoadingSendForgot(false);
                restartTimer();
                if (!isValid) {
                    toast(
                        msg,
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );
                    return
                }
                setVerifyEmailPage(true);

                toast(
                    'Reset password link successfully sent to your email!',
                    {
                        autoClose: 1500,
                        type: 'success',
                    },
                );
            });
        }
    });

    const { errors, touched, handleSubmit, getFieldProps } = formik;

    return (
        <UserFirstLoginLayout>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <S.Container>
                        <If condition={verifyEmailPage}>
                            <Then>
                                <BackButton onClick={() => setVerifyEmailPage(false)} />
                                <TokenIcon />
                                <S.VerifyEmailContainer>
                                    <Typography variant="h5">Check your email!</Typography>
                                    <Typography variant="body1" color="grayMedium.main">
                                        We send you an email to reset your password. <br />If you don't find
                                        in your inbox, also check your spam folder.
                                    </Typography>
                                    <S.FormContainer>
                                        <Button
                                            size="large"
                                            disabled={counting}
                                            fullWidth
                                            variant="outlined"
                                            type="submit"
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Resend email
                                            {counting ? ` ( ${timeLeft}s )` : ''}
                                        </Button>
                                    </S.FormContainer>
                                </S.VerifyEmailContainer>
                            </Then>

                            <Else>
                                <Link aria-label="back to login" to="/">
                                    <BackButton />
                                </Link>

                                <S.ForgotPasswordContainer>
                                    <Typography variant="h5">Recover password</Typography>
                                    <Typography mt="10px" variant="body1" color="grayMedium.main">
                                        To recover your password, enter your registered email.
                                    </Typography>

                                    <S.FormContainer>
                                        <S.FormGroup>
                                            <TextField
                                                {...getFieldProps('email')}
                                                hiddenLabel
                                                fullWidth
                                                type="email"
                                                placeholder="example@getthepom.com"
                                                variant="outlined"
                                                error={Boolean(touched.email && errors.email)}
                                                helperText={touched.email && errors.email}
                                            />
                                        </S.FormGroup>

                                        <Button
                                            size="large"
                                            disabled={counting}
                                            variant="contained"
                                            type="submit"
                                        >
                                        {loadingSendForgot ? (
                                            <CircularProgress sx={{ color: '#ffffff' }} size={27} />
                                        ) : (
                                            `CONTINUE  ${counting ? `( ${timeLeft}s )` : ''}`
                                        )}
                                        </Button>
                                    </S.FormContainer>
                                </S.ForgotPasswordContainer>
                            </Else>
                        </If>
                    </S.Container>
                </Form>
            </FormikProvider>
        </UserFirstLoginLayout>
    );
};